import { GoogleAuthProvider, OAuthProvider } from "firebase/auth";

// One place for everything that differs between the OAuth buttons, so login and
// signup cannot drift apart. They previously built providers, mapped errors and
// derived labels separately, which meant a scope added on one path silently did
// not apply on the other.

const PROVIDERS = {
  google: { label: "Google" },
  apple: { label: "Apple" },
};

/**
 * @param {string} providerId "google" or "apple".
 * @return {string} Name to show the user.
 */
export function oauthLabel(providerId) {
  return (PROVIDERS[providerId] || PROVIDERS.google).label;
}

/**
 * Apple only hands over the name on the very first authorization, and only if
 * these scopes are asked for. Firebase copies it onto the user record then; on
 * every later sign-in displayName comes back empty.
 *
 * @param {string} providerId "google" or "apple".
 * @return {object} A Firebase auth provider.
 */
export function buildOAuthProvider(providerId) {
  if (providerId === "apple") {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    return provider;
  }
  return new GoogleAuthProvider();
}

// Apple's "Hide My Email" gives us a per-app relay address instead of the real
// one. It is a stable identity for this app, so signing in with it works — but
// it matches nothing if the person already has an account under their real
// address, and they end up with a silent duplicate. We cannot resolve that for
// them (we are not told the real address), so the least we can do is say so.
const PRIVATE_RELAY_DOMAIN = "privaterelay.appleid.com";

/**
 * @param {string} email Address from the provider.
 * @return {boolean} Whether Apple is hiding the real address behind a relay.
 */
export function isPrivateRelayEmail(email) {
  return String(email || "").toLowerCase().trim().endsWith(`@${PRIVATE_RELAY_DOMAIN}`);
}

/**
 * Turn a Firebase OAuth error into something worth showing someone.
 *
 * @param {object} err The thrown error.
 * @param {string} label Provider name, for the fallback copy.
 * @return {string} User-facing message.
 */
export function describeOAuthError(err, label) {
  switch (err?.code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled";
    case "auth/popup-blocked":
      return "Pop-up blocked. Please allow pop-ups for this site";
    case "auth/network-request-failed":
      return "Network error. Please check your connection";
    case "auth/account-exists-with-different-credential":
      // Reachable on the signup page too now that a second provider shares the
      // email namespace, so the copy has to point somewhere rather than invite
      // a retry that can never succeed.
      return "An account already exists with this email. Sign in with the method you used originally.";
    case "auth/unauthorized-domain":
    case "auth/operation-not-allowed":
      return "Unable to sign in at this time. Please try again later";
    default:
      return `Unable to sign in with ${label}. Please try again`;
  }
}

/**
 * Split a provider displayName into first and last.
 *
 * Everything after the first word is the surname: a naive two-way destructure
 * drops all but the second word, so "Mary Jane van der Berg" became "Jane".
 *
 * @param {string} displayName Name from the provider, possibly empty.
 * @return {{firstName: string, lastName: string}} Split name.
 */
export function splitDisplayName(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
  };
}
